<script lang="ts" setup>
import MainLayout from '@/common/components/MainLayout.vue';
import { Card, Button, DataTable, Column, Menu, type DataTableRowClickEvent, type DataTableRowDoubleClickEvent } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import InputText from 'primevue/inputtext';
import EditCoilDialog from './components/EditCoilDialog.vue';
import DeleteCoilDialog from './components/DeleteCoilDialog.vue';
import { useCoilStore } from 'shared/coils/stores/coilStore';
import { Assert } from 'shared/assert';
import { useRefreshCoilStore } from './stores/refreshCoilStore';

const coilStore = useCoilStore();
const refreshCoilStore = useRefreshCoilStore();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.coils.page);

const filters = ref({
  global: {
    value: null,
    matchMode: "contains"
  }
});

const coils = computed(() => {
  const result = coilStore.coils.map<CoilItem>(c => {
    return {
      id: c.id,
      name: c.name,
      nameSortOrder: 0
    };
  });

  result.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    if (item) {
      item.nameSortOrder = i;
    }
  }

  return result;
});
const selectedCoil = ref<CoilItem[]>([]);
const loading = ref(false);
const menuCoilId = ref<string>();

const menu = useTemplateRef('coil-menu');
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');

const menuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuCoilId.value;
      if (!id) return;
      showCoil(id);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuCoilId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    }
  }
]);

onMounted(() => {
  refreshCoilStore.initialize();
});

function onAddNewClick() {
  editDialog.value?.show({});
}

function onClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.coil-name');
  if (!name) return;
  showCoil(ev.data.id);
}

function onDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const coil = ev.data as CoilItem;
  showCoil(coil.id);
}

async function showCoil(id: string) {
  const coil = await coilStore.getById(id);
  Assert.definied(coil);
  editDialog.value?.show(coil);
}

function toggleMenu(ev: Event, coilId: string) {
  menuCoilId.value = coilId;
  menu.value?.toggle(ev);
}

interface CoilItem {
  id: string;
  name: string;
  nameSortOrder: number;
}

</script>

<template>
  <MainLayout>
    <Card class="coil-card">
      <template #title>
        <span>{{ pageTranslations.title }}</span>
        <Button class="add-button" :label="translations.generic.add" @click="onAddNewClick">
        </Button>
        <InputText class="search-field" autofocus v-model="filters.global.value"
          :placeholder="translations.generic.search" />
      </template>
      <template #content>
        <DataTable class="coil-table" sortField="name" v-model:selection="selectedCoil" v-model:filters="filters"
          :value="coils" data-key="id" scrollable scrollHeight="flex" meta-key-selection @row-dblclick="onDoubleClick"
          :loading="loading" @row-click="onClick">
          <template #empty>{{ pageTranslations.notFound }}</template>
          <template #loading>{{ pageTranslations.loading }}</template>
          <Column field="name" class="coil-name" :header="translations.generic.name" sortable
            sort-field="nameSortOrder"></Column>
          <Column class="w-24 !text-end">
            <template #body="coil">
              <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
                @click="(ev) => toggleMenu(ev, coil.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </MainLayout>

  <EditCoilDialog ref="edit-dialog"></EditCoilDialog>
  <DeleteCoilDialog ref="delete-dialog"></DeleteCoilDialog>
  <Menu ref="coil-menu" :model="menuItems" :popup="true" />

</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto 1rem auto;
  column-gap: 0.5rem;
  align-items: center;
}

.add-button {
  grid-column: 3;
}

.search-field {
  grid-column: 5;
}

:deep(.coil-name) {
  cursor: pointer;
}

:deep(.coil-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.coil-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
